import { Search } from "lucide-react";

import { PERMISSIONS } from "@/api/permissions";
import { Can } from "@/components/Can";
import { Kbd } from "@/components/Kbd";
import { Button } from "@/components/ui/button";
import { isMac } from "@/lib/use-keyboard-shortcuts";

/**
 * The header's search box — a button styled like an input that opens the
 * ⌘K palette (`CommandPalette`), which is the one search UI: users by
 * email, name, phone or id, plus page navigation. Renders only for roles
 * that can open user records; everyone else still has ⌘K for pages.
 */
export function UserSearchBox({ onOpen }: { onOpen: () => void }) {
  return (
    <Can permission={PERMISSIONS.USERS_READ}>
      <button
        type="button"
        onClick={onOpen}
        aria-label="Search users"
        aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
        className="border-input bg-background text-muted-foreground hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full items-center gap-2 rounded-lg border px-2.5 text-sm transition-colors outline-none focus-visible:ring-3"
      >
        <Search className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">
          Search users — email, name, phone, ID
        </span>
        <span className="hidden items-center gap-0.5 sm:inline-flex">
          <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
    </Can>
  );
}

/** The same trigger as an icon, for the header below `md` where the box is hidden. */
export function UserSearchIconButton({ onOpen }: { onOpen: () => void }) {
  return (
    <Can permission={PERMISSIONS.USERS_READ}>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Search users"
        title="Search users (⌘K)"
        className="md:hidden"
        onClick={onOpen}
      >
        <Search className="size-4" />
      </Button>
    </Can>
  );
}
